import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Button from '../common/Button';
import betslipService from '../../services/betslipService';
import api from '../../services/api';

const ManualBetSlipModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Sport/Game, 2: Bet Details, 3: Stake/Submit

  // Step 1: Sport & Game Selection
  const [sport, setSport] = useState('nba');
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  // Step 2: Bet Details
  const [betType, setBetType] = useState('spread'); // 'spread', 'moneyline', 'total'
  const [selectedTeam, setSelectedTeam] = useState(null); // 'home' or 'away'
  const [spreadLine, setSpreadLine] = useState('');
  const [totalLine, setTotalLine] = useState('');
  const [overUnder, setOverUnder] = useState('over'); // 'over' or 'under'
  const [odds, setOdds] = useState('-110');

  // Step 3: Stake & Extras
  const [stakeAmount, setStakeAmount] = useState('');
  const [isBonusBet, setIsBonusBet] = useState(false);
  const [profitBoost, setProfitBoost] = useState('');
  const [sportsbook, setSportsbook] = useState('DraftKings');
  const [source, setSource] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [notes, setNotes] = useState('');

  const sports = [
    { value: 'nba', label: 'NBA' },
    { value: 'nfl', label: 'NFL' },
    { value: 'nhl', label: 'NHL' },
    { value: 'ncaa_b', label: 'NCAAB' },
    { value: 'ncaa_f', label: 'NCAAF' },
  ];

  const sportsbooks = ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPN Bet', 'Bet365', 'Other'];

  const fetchGames = useCallback(async () => {
    setLoadingGames(true);
    setSelectedGame(null);
    try {
      // Fetch today's games for the selected sport
      const response = await api.get(`/games/${sport}/today`);
      setGames(response.data.games || []);
    } catch (error) {
      console.error('Error fetching games:', error);
      setGames([]);
    } finally {
      setLoadingGames(false);
    }
  }, [sport]);

  // Fetch games when sport changes
  useEffect(() => {
    if (isOpen && sport) {
      fetchGames();
    }
  }, [sport, isOpen, fetchGames]);

  // Calculate payout based on odds, stake, and profit boost
  const calculatedPayout = useMemo(() => {
    const stake = parseFloat(stakeAmount) || 0;
    let oddsNum = parseFloat(odds) || 0;
    const boost = parseFloat(profitBoost) || 0;

    if (stake <= 0 || oddsNum === 0) return null;

    let profit = 0;
    if (oddsNum > 0) {
      // Positive odds: profit = stake * (odds / 100)
      profit = stake * (oddsNum / 100);
    } else {
      // Negative odds: profit = stake / (|odds| / 100)
      profit = stake / (Math.abs(oddsNum) / 100);
    }

    // Apply profit boost
    if (boost > 0) {
      profit = profit * (1 + boost / 100);
    }

    // For bonus bets, you don't get stake back
    const totalPayout = isBonusBet ? profit : stake + profit;

    return {
      profit: profit.toFixed(2),
      totalPayout: totalPayout.toFixed(2),
      boostedProfit: boost > 0 ? (profit - (profit / (1 + boost / 100))).toFixed(2) : null
    };
  }, [stakeAmount, odds, profitBoost, isBonusBet]);

  // Get selection display text
  const getSelectionText = () => {
    if (!selectedGame) return '';

    if (betType === 'total') {
      return `${overUnder === 'over' ? 'Over' : 'Under'} ${totalLine}`;
    }

    const team = selectedTeam === 'home' ? selectedGame.home_team : selectedGame.away_team;
    if (betType === 'spread') {
      const line = parseFloat(spreadLine) || 0;
      return `${team?.abbreviation || team?.name} ${line >= 0 ? '+' : ''}${line}`;
    }
    return `${team?.abbreviation || team?.name} ML`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const legData = {
        sport,
        bet_type: betType,
        game_id: selectedGame?.id,
        home_team_name: selectedGame?.home_team?.name,
        away_team_name: selectedGame?.away_team?.name,
        game_date: selectedGame?.date,
        selection: betType === 'total' ? overUnder : selectedTeam,
        line_value: betType === 'spread' ? parseFloat(spreadLine) :
                    betType === 'total' ? parseFloat(totalLine) : null,
        odds: parseFloat(odds)
      };

      // Use local date (not UTC) to avoid timezone shifting the date by a day
      const now = new Date();
      const localBetDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const betslipData = {
        bet_date: localBetDate,
        betslip_type: 'single',
        sportsbook,
        stake_amount: parseFloat(stakeAmount),
        total_odds: parseFloat(odds),
        potential_payout: calculatedPayout ? parseFloat(calculatedPayout.totalPayout) : null,
        is_bonus_bet: isBonusBet,
        profit_boost_pct: profitBoost ? parseFloat(profitBoost) : null,
        notes,
        source: source || null,
        source_url: sourceUrl || null,
        legs: [legData]
      };

      const result = await betslipService.createBetslipManually(betslipData);

      if (onSuccess) {
        onSuccess(result);
      }

      // Reset form
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating betslip:', error);
      alert(error.response?.data?.detail || 'Failed to create betslip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSport('nba');
    setSelectedGame(null);
    setBetType('spread');
    setSelectedTeam(null);
    setSpreadLine('');
    setTotalLine('');
    setOverUnder('over');
    setOdds('-110');
    setStakeAmount('');
    setIsBonusBet(false);
    setProfitBoost('');
    setSource('');
    setSourceUrl('');
    setNotes('');
  };

  const canProceedToStep2 = selectedGame !== null;
  const canProceedToStep3 = betType && odds &&
    (betType === 'moneyline' ? selectedTeam :
     betType === 'spread' ? selectedTeam && spreadLine :
     betType === 'total' ? totalLine : false);
  const canSubmit = stakeAmount && parseFloat(stakeAmount) > 0;

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-card border border-border rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-text-primary">Create Bet</h2>
            <div className="flex items-center gap-2 mt-1">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step >= s ? 'bg-accent-blue text-white' : 'bg-bg-primary text-text-secondary'
                  }`}>
                    {s}
                  </div>
                  {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-accent-blue' : 'bg-border'}`} />}
                </div>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Step 1: Sport & Game Selection */}
            {step === 1 && (
              <>
                {/* Sport Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Select Sport
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {sports.map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setSport(s.value)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          sport === s.value
                            ? 'bg-accent-blue text-white'
                            : 'bg-bg-primary text-text-primary border border-border hover:border-accent-blue'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Game Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Select Game
                  </label>
                  {loadingGames ? (
                    <div className="text-center py-8 text-text-secondary">
                      Loading games...
                    </div>
                  ) : games.length === 0 ? (
                    <div className="text-center py-8 text-text-secondary">
                      No games scheduled for today
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {games.map(game => (
                        <button
                          key={game.id}
                          type="button"
                          onClick={() => setSelectedGame(game)}
                          className={`w-full p-3 rounded-lg text-left transition-all ${
                            selectedGame?.id === game.id
                              ? 'bg-accent-blue/20 border-2 border-accent-blue'
                              : 'bg-bg-primary border border-border hover:border-accent-blue'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="text-sm">
                                <span className="font-medium text-text-primary">
                                  {game.away_team?.abbreviation || game.away_team?.name}
                                </span>
                                <span className="text-text-secondary mx-2">@</span>
                                <span className="font-medium text-text-primary">
                                  {game.home_team?.abbreviation || game.home_team?.name}
                                </span>
                              </div>
                            </div>
                            <span className="text-xs text-text-secondary">
                              {game.time || game.status}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 2: Bet Type & Selection */}
            {step === 2 && selectedGame && (
              <>
                {/* Game Info */}
                <div className="bg-bg-primary rounded-lg p-3 border border-border">
                  <div className="text-sm text-text-secondary mb-1">Selected Game</div>
                  <div className="font-medium text-text-primary">
                    {selectedGame.away_team?.name} @ {selectedGame.home_team?.name}
                  </div>
                </div>

                {/* Bet Type */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Bet Type
                  </label>
                  <div className="flex gap-2">
                    {['spread', 'moneyline', 'total'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setBetType(type);
                          setSelectedTeam(null);
                        }}
                        className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all capitalize ${
                          betType === type
                            ? 'bg-accent-blue text-white'
                            : 'bg-bg-primary text-text-primary border border-border hover:border-accent-blue'
                        }`}
                      >
                        {type === 'total' ? 'Over/Under' : type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Team Selection (for spread/moneyline) */}
                {(betType === 'spread' || betType === 'moneyline') && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Select Team
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedTeam('away')}
                        className={`p-4 rounded-lg text-center transition-all ${
                          selectedTeam === 'away'
                            ? 'bg-accent-blue/20 border-2 border-accent-blue'
                            : 'bg-bg-primary border border-border hover:border-accent-blue'
                        }`}
                      >
                        <div className="font-medium text-text-primary">
                          {selectedGame.away_team?.abbreviation || selectedGame.away_team?.name}
                        </div>
                        <div className="text-xs text-text-secondary mt-1">Away</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedTeam('home')}
                        className={`p-4 rounded-lg text-center transition-all ${
                          selectedTeam === 'home'
                            ? 'bg-accent-blue/20 border-2 border-accent-blue'
                            : 'bg-bg-primary border border-border hover:border-accent-blue'
                        }`}
                      >
                        <div className="font-medium text-text-primary">
                          {selectedGame.home_team?.abbreviation || selectedGame.home_team?.name}
                        </div>
                        <div className="text-xs text-text-secondary mt-1">Home</div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Spread Line (only for spread) */}
                {betType === 'spread' && selectedTeam && (
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      Spread Line
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={spreadLine}
                      onChange={(e) => setSpreadLine(e.target.value)}
                      placeholder="e.g., -3.5 or +7"
                      className="w-full px-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                  </div>
                )}

                {/* Total Line (for over/under) */}
                {betType === 'total' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Over or Under
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setOverUnder('over')}
                          className={`p-3 rounded-lg text-center transition-all ${
                            overUnder === 'over'
                              ? 'bg-success/20 border-2 border-success'
                              : 'bg-bg-primary border border-border hover:border-success'
                          }`}
                        >
                          <span className="font-medium text-text-primary">Over</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setOverUnder('under')}
                          className={`p-3 rounded-lg text-center transition-all ${
                            overUnder === 'under'
                              ? 'bg-danger/20 border-2 border-danger'
                              : 'bg-bg-primary border border-border hover:border-danger'
                          }`}
                        >
                          <span className="font-medium text-text-primary">Under</span>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Total Line
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        value={totalLine}
                        onChange={(e) => setTotalLine(e.target.value)}
                        placeholder="e.g., 225.5"
                        className="w-full px-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                      />
                    </div>
                  </>
                )}

                {/* Odds */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Odds (American)
                  </label>
                  <input
                    type="number"
                    value={odds}
                    onChange={(e) => setOdds(e.target.value)}
                    placeholder="-110"
                    className="w-full px-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  />
                </div>
              </>
            )}

            {/* Step 3: Stake & Submit */}
            {step === 3 && (
              <>
                {/* Bet Summary */}
                <div className="bg-bg-primary rounded-lg p-4 border border-border">
                  <div className="text-sm text-text-secondary mb-1">Your Bet</div>
                  <div className="font-medium text-text-primary text-lg">
                    {getSelectionText()}
                  </div>
                  <div className="text-sm text-text-secondary mt-1">
                    {selectedGame?.away_team?.name} @ {selectedGame?.home_team?.name}
                  </div>
                  <div className="text-sm text-accent-blue mt-1">
                    Odds: {odds > 0 ? '+' : ''}{odds}
                  </div>
                </div>

                {/* Sportsbook */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Sportsbook
                  </label>
                  <select
                    value={sportsbook}
                    onChange={(e) => setSportsbook(e.target.value)}
                    className="w-full px-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                  >
                    {sportsbooks.map(book => (
                      <option key={book} value={book}>{book}</option>
                    ))}
                  </select>
                </div>

                {/* Stake Amount with Bonus Bet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-text-secondary">
                      Stake Amount
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isBonusBet}
                        onChange={(e) => setIsBonusBet(e.target.checked)}
                        className="w-4 h-4 rounded border-border text-accent-blue focus:ring-accent-blue"
                      />
                      <span className="text-sm text-text-secondary">Bonus Bet</span>
                    </label>
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-3 bg-bg-primary border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                  </div>
                  {isBonusBet && (
                    <p className="text-xs text-warning mt-1">
                      Bonus bet: Only winnings are paid out, not the stake
                    </p>
                  )}
                </div>

                {/* Profit Boost */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Profit Boost % (Optional)
                  </label>
                  <div className="flex gap-2">
                    {[10, 20, 25, 30, 50, 100].map(boost => (
                      <button
                        key={boost}
                        type="button"
                        onClick={() => setProfitBoost(profitBoost === String(boost) ? '' : String(boost))}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                          profitBoost === String(boost)
                            ? 'bg-success text-white'
                            : 'bg-bg-primary text-text-secondary border border-border hover:border-success'
                        }`}
                      >
                        {boost}%
                      </button>
                    ))}
                    <input
                      type="number"
                      value={profitBoost}
                      onChange={(e) => setProfitBoost(e.target.value)}
                      placeholder="Other"
                      className="w-20 px-2 py-1.5 text-sm bg-bg-primary border border-border rounded text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                  </div>
                </div>

                {/* Calculated Payout */}
                {calculatedPayout && (
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Potential Profit</span>
                      <span className="font-bold text-success text-lg">
                        ${calculatedPayout.profit}
                        {calculatedPayout.boostedProfit && (
                          <span className="text-xs ml-1 text-success/70">
                            (+${calculatedPayout.boostedProfit} boost)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-text-secondary">Total Payout</span>
                      <span className="font-bold text-text-primary text-xl">
                        ${calculatedPayout.totalPayout}
                      </span>
                    </div>
                  </div>
                )}

                {/* Source (collapsible) */}
                <details className="bg-bg-primary rounded-lg border border-border">
                  <summary className="p-3 cursor-pointer text-sm font-medium text-text-secondary">
                    Source / Handicapper (Optional)
                  </summary>
                  <div className="p-3 pt-0 space-y-3">
                    <input
                      type="text"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      placeholder="e.g., @SharpPlays, BettingPros"
                      className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                    <input
                      type="url"
                      value={sourceUrl}
                      onChange={(e) => setSourceUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2 bg-bg-card border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue"
                    />
                  </div>
                </details>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes..."
                    rows={2}
                    className="w-full px-4 py-2 bg-bg-primary border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-blue resize-none"
                  />
                </div>
              </>
            )}
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="flex gap-3 p-4 border-t border-border flex-shrink-0">
          {step > 1 && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStep(step - 1)}
              disabled={loading}
            >
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !canProceedToStep2 : !canProceedToStep3}
              className="flex-1"
            >
              Continue
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              className="flex-1"
            >
              {loading ? 'Creating...' : 'Create Bet'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ManualBetSlipModal;
